/* Expense > Approvals — the approver inbox (Phase 1). Wired to
   GET /v1/expense/approvals/inbox; each card is a report routed to the caller. Approve
   posts immediately; Reject requires a comment (so the requester knows what to fix) and
   is revealed inline. Separation-of-duties + routing are enforced server-side — a
   requester can never see or act on their own report here. Final approval triggers the
   server-side ledger posting (the D7 path); the UI just records the decision. */
import { useState } from 'react';
import { Link } from 'react-router';
import { Button, Card } from '@andavo/web-ui';

import {
  useApprovalInbox,
  useDecideApproval,
  type ExpenseApproval,
} from './hooks/use-approvals';
import { Field } from './ui/field';

function shortOid(oid: string): string {
  return oid.slice(0, 8);
}

export function ApprovalsScreen() {
  const { data: inbox = [], isLoading, isError } = useApprovalInbox();

  if (isLoading) {
    return (
      <p className="py-10 text-content-secondary text-sm">Loading inbox…</p>
    );
  }
  if (isError) {
    return (
      <p className="py-10 text-content-danger text-sm">
        Couldn’t load your approval inbox. Try again.
      </p>
    );
  }
  if (inbox.length === 0) {
    return (
      <Card className="p-10 text-center">
        <p className="font-inter text-content-secondary text-sm">
          Nothing awaiting your approval. You’re all caught up.
        </p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {inbox.map((a) => (
        <ApprovalCard key={a.approvalId} approval={a} />
      ))}
    </div>
  );
}

function ApprovalCard({ approval }: { approval: ExpenseApproval }) {
  const decide = useDecideApproval();
  const [rejecting, setRejecting] = useState(false);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = (decision: 'APPROVED' | 'REJECTED') => {
    setError(null);
    decide.mutate(
      {
        approvalId: approval.approvalId,
        decision,
        comment: comment || undefined,
      },
      {
        onError: (err) =>
          setError(err instanceof Error ? err.message : 'Decision failed.'),
      },
    );
  };

  return (
    <Card
      className="flex flex-col gap-4 p-5"
      data-approval-card={approval.reportId}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <Link
            to={`/expense/reports/${approval.reportId}`}
            className="font-sans font-semibold text-content-primary text-base hover:text-content-action"
          >
            Report #{approval.reportId}
          </Link>
          <p className="font-inter text-content-secondary text-sm">
            Submitted by {shortOid(approval.requesterUserOid)} · approval step{' '}
            {approval.seq}
          </p>
        </div>
        {!rejecting && (
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={decide.isPending}
              onClick={() => setRejecting(true)}
            >
              Reject
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={decide.isPending}
              onClick={() => submit('APPROVED')}
            >
              Approve
            </Button>
          </div>
        )}
      </div>

      {rejecting && (
        <div className="flex flex-col gap-3 border-border-subtle border-t pt-4">
          <Field
            label="Reason for rejection"
            htmlFor={`reject-${approval.approvalId}`}
            hint="The requester sees this so they know what to fix."
          >
            <textarea
              id={`reject-${approval.approvalId}`}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-border-subtle bg-surface-background px-3 py-2 font-inter text-content-primary text-sm outline-none focus:border-border-action focus:ring-2 focus:ring-border-action/30"
            />
          </Field>
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setRejecting(false);
                setComment('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              disabled={decide.isPending || !comment.trim()}
              onClick={() => submit('REJECTED')}
            >
              Reject report
            </Button>
          </div>
        </div>
      )}

      {error && (
        <p className="font-inter text-content-danger text-sm">{error}</p>
      )}
    </Card>
  );
}
